import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';

const VendorEdit = () => {
  const { vendorId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [vendor, setVendor] = useState(null);
  const [countries, setCountries] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    _id: '',
    first_name: '',
    last_name: '',
    phone: '',
    countryId: '',
    business_name: '',
    address: '',
    about: '',
    services: [],
    image: null
  });

  const [passwordData, setPasswordData] = useState({
    _id: '',
    new_password: '',
    confirm_password: ''
  });

  const serviceOptions = [
    { value: 'packages', label: 'Holiday Packages' },
    { value: 'vehicles', label: 'Vehicles' },
    { value: 'hotels', label: 'Hotels/Cottage/Cabins etc.' },
    { value: 'guide', label: 'Guide' },
    { value: 'photographer', label: 'Photographer' },
    { value: 'visaConsultancy', label: 'Visa Consultancy' },
    { value: 'activities', label: 'Activities' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchVendorDetails();
    fetchCountries();
  }, [vendorId]);

  const fetchVendorDetails = async () => {
    try {
      const response = await apiService.get(`view_vendor_application/${vendorId}`);
      if (response && response.data) {
        const data = response.data.data;
        setVendor(data);
        setFormData({
          _id: data._id,
          first_name: data.first_name || '',
          last_name: data.last_name || '',
          phone: data.phone || '',
          countryId: data.countryId?._id || '',
          business_name: data.business_name || '',
          address: data.address || '',
          about: data.about || '',
          services: data.services || [],
          image: null
        });
        setPasswordData({ ...passwordData, _id: data._id });
        if (data.image) {
          setImagePreview(`${process.env.REACT_APP_MEDIA_URL}${data.image}`);
        }
      }
    } catch (error) {
      toast.error('Failed to load vendor details');
      console.error('Vendor details fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await apiService.get('view_vendor_application/' + vendorId);
      if (response && response.data && response.data.country) {
        setCountries(response.data.country);
      }
    } catch (error) {
      console.error('Countries fetch error:', error);
    }
  };
  console.log("Ignore");
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleServiceChange = (value) => {
    let newServices = [...formData.services];
    if (value === 'other') {
      newServices = newServices.includes('other') ? [] : ['other'];
    } else {
      if (newServices.includes(value)) {
        newServices = newServices.filter(s => s !== value);
      } else {
        newServices = newServices.filter(s => s !== 'other');
        newServices.push(value);
      }
    }
    setFormData({ ...formData, services: newServices });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be 5MB or less');
        e.target.value = '';
        return;
      }
      setFormData({ ...formData, image: file });
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (key === 'services') {
          formData.services.forEach(service => {
            submitData.append('services[]', service);
          });
        } else if (key === 'image') {
          if (formData.image) {
            submitData.append('image', formData.image);
          }
        } else {
          submitData.append(key, formData[key]);
        }
      });

      const response = await apiService.postFormData('updateVendorProfile', submitData);
      if (response.status) {
        toast.success('Profile updated successfully');
        fetchVendorDetails();
      } else {
        toast.error(response.message || 'Failed to update profile');
      }
    } catch (error) {
      toast.error('Failed to update profile');
      console.error('Update error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    try {
      const response = await apiService.post('updatePassword', passwordData);
      if (response.status) {
        toast.success('Password updated successfully');
        setShowPasswordModal(false);
        setPasswordData({ _id: vendor._id, new_password: '', confirm_password: '' });
      } else {
        toast.error(response.message || 'Failed to update password');
      }
    } catch (error) {
      toast.error('Failed to update password');
      console.error('Password update error:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#009ef7]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Breadcrumb */}
      <div className="mb-4">
        <nav className="text-sm text-gray-600">
          <Link to="/" className="hover:text-[#009ef7]">Home</Link>
          <span className="mx-2">/</span>
          <Link to="/vendors" className="hover:text-[#009ef7]">Vendors</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-900">Vendor Profile</span>
        </nav>
      </div>

      {/* Navigation Badges */}
      {user?.role === 'admin' && (
        <div className="flex gap-3 mb-5">
          <Link to={`/vendorWallet/${vendorId}`} className="inline-flex items-center px-2 sm:px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 no-underline">
            <i className="bi bi-wallet2 mr-2"></i>Wallet
          </Link>
          <Link to={`/vendorSubscriptionList/${vendorId}`} className="inline-flex items-center px-2 sm:px-4 py-2 bg-[#009ef7]/10 text-blue-700 rounded-lg hover:bg-[#009ef7]/10 no-underline">
            <i className="bi bi-arrow-left-right mr-2"></i>Transactions
          </Link>
          <Link to={`/vendor/activity/${vendorId}`} className="inline-flex items-center px-2 sm:px-4 py-2 bg-violet-50 text-violet-700 rounded-lg hover:bg-violet-100 no-underline">
            <i className="bi bi-activity mr-2"></i>Activity
          </Link>
        </div>
      )}

      <div className="flex flex-col xl:flex-row gap-6">
        {/* Sidebar */}
        <div className="w-full xl:w-80 flex-shrink-0">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex flex-col items-center mb-5">
              <div className="w-32 h-32 rounded-full overflow-hidden mb-4">
                <img 
                  src={imagePreview || '/assets/media/avatars/300-1.jpg'} 
                  alt="Vendor" 
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-bold text-gray-800 text-center">{vendor?.first_name} {vendor?.last_name}</h3>
              <div className="flex flex-col gap-2 mt-2 w-full">
                <a 
                  href={`https://www.travellinglark.com/profile/${vendor?.slug}/${vendor?.id}`} 
                  target="_blank" 
                  rel="noreferrer"
                  className="inline-flex items-center justify-center px-3 py-1 bg-cyan-50 text-cyan-700 rounded-full text-sm hover:bg-cyan-100 no-underline"
                >
                  <i className="bi bi-building mr-1"></i>Business Profile
                </a>
                <span className="inline-flex items-center justify-center px-3 py-1 bg-green-50 text-green-700 rounded-full text-sm font-semibold">
                  {vendor?.role?.charAt(0).toUpperCase() + vendor?.role?.slice(1)}
                </span>
              </div>
            </div>

            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="font-bold text-gray-900">Details</h4>
                {vendor?.doc && (
                  <a 
                    href={`${process.env.REACT_APP_MEDIA_URL}${vendor.doc}`} 
                    target="_blank" 
                    rel="noreferrer"
                    className="text-xs px-2 py-1 bg-cyan-50 text-cyan-700 rounded no-underline"
                  >
                    View Document
                  </a>
                )}
              </div>

              <div className="space-y-3 text-sm">
                <div>
                  <div className="font-semibold text-gray-900">Account ID</div>
                  <div className="text-gray-600">ID-{vendor?._id}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Business Name</div>
                  <div className="text-gray-600">{vendor?.business_name}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Email</div>
                  <div className="text-gray-600">{vendor?.email}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Phone</div>
                  <div className="text-gray-600">{vendor?.phone}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Address</div>
                  <div className="text-gray-600">{vendor?.address}</div>
                </div>
                <div>
                  <div className="font-semibold text-gray-900">Country</div>
                  <div className="text-gray-600">{vendor?.countryId?.name} {vendor?.stateId?.name && `-- ${vendor.stateId.name}`}</div>
                </div>
                {vendor?.social && (
                  <div>
                    <div className="font-semibold text-gray-900">Social Media Link</div>
                    <a href={vendor.social.startsWith('http') ? vendor.social : `http://${vendor.social}`} target="_blank" rel="noreferrer" className="text-[#009ef7] hover:underline break-all">{vendor.social}</a>
                  </div>
                )}
                {vendor?.website && (
                  <div>
                    <div className="font-semibold text-gray-900">Website Link</div>
                    <a href={vendor.website.startsWith('http') ? vendor.website : `http://${vendor.website}`} target="_blank" rel="noreferrer" className="text-[#009ef7] hover:underline break-all">{vendor.website}</a>
                  </div>
                )}
                <div>
                  <div className="font-semibold text-gray-900">Joining Date</div>
                  <div className="text-gray-600">{new Date(vendor?.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' })}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1">
          {/* Profile Form */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="border-b p-6">
              <h2 className="text-xl font-bold text-gray-900">Profile</h2>
            </div>
            <div className="p-6">
              <form onSubmit={handleSubmit}>
                {/* Image Upload */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Update Logo
                    <span className="ml-1 text-gray-500 text-xs">(Allowed: png, jpg, jpeg)</span>
                  </label>
                  <div className="relative w-32 h-32 rounded-lg overflow-hidden border-2 border-gray-200 group">
                    <img 
                      src={imagePreview || '/assets/media/avatars/300-1.jpg'} 
                      alt="Preview" 
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute top-1 right-1 w-8 h-8 bg-gray-600 text-white rounded-full hover:bg-gray-700 flex items-center justify-center shadow-lg"
                      title="Change image"
                    >
                      <i className="bi bi-pencil text-sm"></i>
                    </button>
                    {formData.image && (
                      <button
                        type="button"
                        onClick={() => {
                          setImagePreview(vendor?.image ? `${process.env.REACT_APP_MEDIA_URL}${vendor.image}` : null);
                          setFormData({ ...formData, image: null });
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        className="absolute bottom-1 right-1 w-8 h-8 bg-red-600 text-white rounded-full hover:bg-red-700 flex items-center justify-center shadow-lg"
                        title="Remove image"
                      >
                        <i className="bi bi-x text-lg"></i>
                      </button>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".png,.jpg,.jpeg"
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </div>
                </div>

                {/* Name Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">First Name <span className="text-red-500">*</span></label>
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                    />
                  </div>
                </div>

                {/* Phone & Country */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Country <span className="text-red-500">*</span></label>
                    <select
                      name="countryId"
                      value={formData.countryId}
                      onChange={handleInputChange}
                      className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                      required
                    >
                      <option value="">Select Country</option>
                      {countries.map(country => (
                        <option key={country._id} value={country._id}>{country.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Business Name */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Business Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="business_name"
                    value={formData.business_name}
                    onChange={handleInputChange}
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                    required
                  />
                </div>

                {/* Address */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Address <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>

                {/* About */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">About</label>
                  <textarea
                    name="about"
                    value={formData.about}
                    onChange={handleInputChange}
                    rows="5"
                    placeholder="Describe your services, including hotel packages, vehicles, and tour guides, along with details about yourself..."
                    className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  />
                </div>

                {/* Services */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Services <span className="text-red-500">*</span></label>
                  <div className="flex flex-wrap gap-4">
                    {serviceOptions.map(service => (
                      <label key={service.value} className="inline-flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.services.includes(service.value)}
                          onChange={() => handleServiceChange(service.value)}
                          className="w-4 h-4 text-[#009ef7] border-gray-300 rounded focus:ring-[#009ef7]"
                        />
                        <span className="ml-2 text-sm text-gray-700">{service.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </form>
            </div>
          </div>

          {/* Security Details */}
          <div className="bg-white rounded-lg shadow">
            <div className="border-b p-6">
              <h2 className="text-xl font-bold text-gray-900">Security Details</h2>
            </div>
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-gray-900">Password</div>
                  <div className="text-gray-600">******</div>
                </div>
                <button
                  onClick={() => setShowPasswordModal(true)}
                  className="px-2 sm:px-4 py-2 bg-[#009ef7]/10 text-blue-700 rounded-lg hover:bg-[#009ef7]/10"
                >
                  <i className="bi bi-pencil mr-2"></i>Edit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Password Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-bold text-gray-900">Update Password</h2>
              <button
                onClick={() => setShowPasswordModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <i className="bi bi-x text-2xl"></i>
              </button>
            </div>
            <form onSubmit={handlePasswordSubmit} className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">New Password</label>
                <input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Use 8 or more characters with a mix of letters, numbers & symbols.</p>
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Confirm New Password</label>
                <input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  className="w-full px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-[#009ef7]"
                  required
                />
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowPasswordModal(false)}
                  className="px-2 sm:px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  Discard
                </button>
                <button
                  type="submit"
                  className="px-2 sm:px-4 py-2 bg-[#009ef7] text-white rounded-lg hover:bg-[#009ef7]/90"
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorEdit;
